/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Select } from 'antd'
import { FC, useState, useEffect } from 'react'
import { CustomFormInstance } from '../../../../types'
import {
    MachineForm,
    MachineNames,
    PrimaryAdditionalComponent,
    PrimaryComponent,
    SubComponent,
} from '../types'

type Props = {
    form: CustomFormInstance<MachineForm>
    name: MachineNames.partsOfIndependentClaim
}

const PartsOfIndependentClaim: FC<Props> = ({ form, name }) => {
    type Step = {
        id?: string
        component?: string
        indefiniteArticle?: string
        mainComponent?: string
        mainComponentId?: string
        disabled?: boolean
    }
    const [steps, setSteps] = useState<Step[]>([])
    useEffect(() => {
        const primaryComponents =
            form.getFieldValue<MachineForm['primaryComponents']>(
                'primaryComponents'
            ) ?? []
        const additionalComponents =
            form.getFieldValue<MachineForm['primaryAdditionalComponents']>(
                'primaryAdditionalComponents'
            ) ?? []
        const subComponents = form.getFieldValue<
            MachineForm['primaryComponentSubComponentsList']
        >('primaryComponentSubComponentsList')
        let normalizedSteps: Step[] = []

        // Add primary components
        normalizedSteps = normalizedSteps.concat(primaryComponents)

        // Add additional components
        if (additionalComponents) {
            const formattedAdditionalComponents = additionalComponents.map(
                (addComp: PrimaryAdditionalComponent) => ({
                    id: `add_${addComp.id}`,
                    component: addComp.component,
                })
            )
            normalizedSteps = normalizedSteps.concat(
                formattedAdditionalComponents
            )
        }

        // Add sub components
        if (subComponents) {
            primaryComponents.forEach((primaryComponent: PrimaryComponent) => {
                let subComponent = subComponents[primaryComponent.id ?? '']
                if (subComponent) {
                    subComponent = subComponent.map(
                        (subComp: SubComponent) => ({
                            ...subComp,
                            mainComponent: primaryComponent.component,
                            mainComponentId: primaryComponent.id,
                        })
                    )
                    normalizedSteps = normalizedSteps.concat(subComponent)
                }
            })
        }
        setSteps(normalizedSteps.filter((step) => !!step))
    }, [form])
    const handleChange = (event: any) => {
        setSteps((prevSteps) =>
            prevSteps.map((step) => {
                if (step.mainComponentId) {
                    if (!event.includes(step.mainComponentId)) {
                        return { ...step, disabled: true }
                    }
                    return { ...step, disabled: false }
                }

                return step
            })
        )
        const stepsArray = steps.map((item: Step) => {
            if (Object.prototype.hasOwnProperty.call(item, 'mainComponentId')) {
                return `${item.mainComponentId}+=>${item.id}`
            }
            return item?.id
        })
        const filteredPartsOfInDependentClaim = event.filter(
            (item: string) =>
                !(item.includes('+=>') && !event.includes(item.split('+=>')[0]))
        )

        form.setFieldsValue({
            [name]: filteredPartsOfInDependentClaim,
        })
        const test = stepsArray.filter(
            (item) => !filteredPartsOfInDependentClaim.includes(item)
        )
        form.setFieldsValue({ partsOfDependentClaim: test })
    }
    return (
        <>
            <Form.Item
                name={name}
                rules={[
                    {
                        validator: async (_, components) => {
                            if (
                                components === undefined ||
                                components?.length === 0
                            )
                                throw new Error('At least one component!')
                        },
                    },
                ]}
                initialValue={(
                    form.getFieldValue<MachineForm['primaryComponents']>(
                        'primaryComponents'
                    ) ?? []
                ).map((comp: PrimaryComponent) => comp.id)}
            >
                <Select
                    mode="multiple"
                    onChange={handleChange}
                    dropdownClassName="always-visible-scrollbar-dropdown"
                    options={steps.map(
                        ({
                            id,
                            component,
                            mainComponentId,
                            mainComponent,
                            disabled,
                        }) => {
                            return {
                                value: mainComponentId
                                    ? `${mainComponentId}+=>${id}`
                                    : id ?? '',
                                label: mainComponent
                                    ? `${component} from the ${mainComponent} component`
                                    : component ?? '',
                                key: mainComponent
                                    ? `${component}+=>${mainComponent}`
                                    : component ?? '',
                                disabled: disabled ?? false,
                            }
                        }
                    )}
                />
            </Form.Item>
        </>
    )
}

export default PartsOfIndependentClaim
